import { Delta } from 'quill/core';
import React from 'react';
import deltaToText from '../editor/utils/deltaToText';
import { gql } from '../graphql/generated/gql';
import type { DocumentType } from '../graphql/generated/gql';
import sortAndRotateById from '../groups/sortAndRotateById';
import clockIcon from '../icons/non-prefilled/16x16/clock.svg';
import groupIcon from '../icons/non-prefilled/16x16/group.svg';
import groupPath from '../utils/url/groupPath';
import AvatarStack from './AvatarStack';
import * as styles from './GroupSquarishCard.module.scss';
import SVGIcon from './SVGIcon';
import SquarishCardBase from './SquarishCardBase';
import Tag from './Tag';
import Timestamp from './Timestamp';

const GROUP_FRAGMENT = gql(`
  fragment GroupSquarishCard_group on Group {
    id
    name
    insertedAt
    description
    banner {
      original
    }
    allUsers {
      id
      ...AvatarStack_users
    }
    owners {
      id
      ...AvatarStack_users
    }
    ...groupPath_group
  }
`);

interface GroupSquarishCardProps {
  group: DocumentType<typeof GROUP_FRAGMENT>;
  link?: boolean;
  overflowMenuItems?: React.ReactNode;
}

const GroupSquarishCard = ({
  group,
  link,
  overflowMenuItems,
}: GroupSquarishCardProps) => {
  const ownerIds = group.owners.map((owner) => owner.id);
  const members = group.allUsers.filter((user) => !ownerIds.includes(user.id));

  return (
    <SquarishCardBase
      icons={
        <div className={styles.groupIcon}>
          <SquarishCardBase.Icon icon={groupIcon} />
        </div>
      }
      avatars={
        <AvatarStack
          items={sortAndRotateById(group.owners, group.id)}
          countOverflowVariant="circle"
          borderSize={3}
          limit={4}
          size={26}
        />
      }
      title={<h4 className={styles.groupName}>{group.name}</h4>}
      banner={
        group.banner?.original ? (
          <img
            alt="Group banner"
            className={styles.bannerImage}
            src={group.banner?.original}
          />
        ) : (
          <div className={styles.plainBanner} />
        )
      }
      description={deltaToText(new Delta(group.description))}
      footer={
        members.length ? (
          <AvatarStack
            items={sortAndRotateById(members, group.id)}
            limit={9}
            countOverflowVariant="circle"
            borderSize={3}
            size={26}
            noWrap
          />
        ) : (
          <Tag
            icon={
              <SVGIcon size={12} className={styles.clockIcon}>
                {clockIcon}
              </SVGIcon>
            }
          >
            Created <Timestamp date={group.insertedAt} />
          </Tag>
        )
      }
      alwaysShowFooter={members.length > 0}
      overflowMenuItems={overflowMenuItems}
      linkTo={link ? groupPath(group) : undefined}
    />
  );
};

export default GroupSquarishCard;
